import { useState, useEffect } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import { processFile, formatFileSize, checkFileSize } from '../../utils/fileCompression'
import { API_BASE_URL, MAX_IMAGES } from '../../config/api'

interface Event {
  id: number
  title: string
  date: string
  location?: string
}

interface GalleryImage {
  id: number
  gallery_id?: number
  event_id: number
  image_path?: string
  image_url?: string
  media_url?: string
  is_thumbnail?: number
  title?: string
  created_at?: string
}

interface EventGallery {
  id?: number
  gallery_id?: number
  event_id: number
  thumbnail_path?: string
  thumbnail_url?: string
  images: GalleryImage[]
}

const EventGalleryManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [events, setEvents] = useState<Event[]>([])
  const [galleries, setGalleries] = useState<EventGallery[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingGallery, setViewingGallery] = useState<EventGallery | null>(null)
  const [updatingGallery, setUpdatingGallery] = useState<EventGallery | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')

  const [formData, setFormData] = useState({
    event_id: '',
    gallery_images: [] as File[],
    thumbnail_index: 0,
    image_titles: [] as string[]
  })


  // Helper function to get full image URL
  const getImageUrl = (image: GalleryImage): string | null => {
    if (image.media_url) return image.media_url
    if (image.image_url) return image.image_url
    if (image.image_path) {
      const cleanPath = image.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return null
  }

  // Fetch events for dropdown
  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}event_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        setEvents(result.data)
      }
    } catch (err) {
      console.error('Error fetching events:', err)
    }
  }

  // Fetch all galleries
  const fetchGalleries = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}event_gallery_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Group images by event_id
        const galleryMap = new Map<number, EventGallery>()
        
        result.data.forEach((image: GalleryImage) => {
          if (!galleryMap.has(image.event_id)) {
            galleryMap.set(image.event_id, {
              id: image.gallery_id,
              gallery_id: image.gallery_id,
              event_id: image.event_id,
              images: [],
              thumbnail_url: image.is_thumbnail ? getImageUrl(image) || undefined : undefined
            })
          }
          
          const gallery = galleryMap.get(image.event_id)!
          gallery.images.push(image)
          
          // Store gallery_id from first image if not set
          if (!gallery.gallery_id && image.gallery_id) {
            gallery.gallery_id = image.gallery_id
            gallery.id = image.gallery_id
          }
          
          if (image.is_thumbnail && !gallery.thumbnail_url) {
            gallery.thumbnail_url = getImageUrl(image) || undefined
          }
        })
        
        setGalleries(Array.from(galleryMap.values()))
      } else {
        setError(result.message || 'Failed to fetch galleries')
        setGalleries([])
      }
    } catch (err) {
      setError('Failed to fetch galleries. Please try again.')
      console.error('Error fetching galleries:', err)
      setGalleries([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
    fetchGalleries()
  }, [])

  // Handle gallery images selection
const handleGalleryImagesChange = async (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  if (!e.target.files || e.target.files.length === 0) return

  const files = Array.from(e.target.files)
  setError('')
  setUploadProgress('')

  // 🔒 Check max image limit
  const existingCount = formData.gallery_images.length
  const allowedCount = MAX_IMAGES - existingCount

  if (allowedCount <= 0) {
    setError(`You can upload a maximum of ${MAX_IMAGES} images.`)
    e.target.value = ''
    return
  }

  if (files.length > allowedCount) {
    setError(
      `Only ${allowedCount} more image(s) allowed. Max ${MAX_IMAGES} images.`
    )
  }

  // Process only allowed number of files
  const filesToProcess = files.slice(0, allowedCount)

  const processedFiles: File[] = []
  let hasError = false

  for (let i = 0; i < filesToProcess.length; i++) {
    const file = filesToProcess[i]

    // Check file size
    const sizeCheck = checkFileSize(file)
    if (!sizeCheck.valid) {
      setError(`${file.name}: ${sizeCheck.message || 'File is too large'}`)
      hasError = true
      continue
    }

    setUploadProgress(
      `Processing ${i + 1}/${filesToProcess.length}: ${file.name}...`
    )

    // Process file (compress if image)
    const result = await processFile(file)

    if (result.error) {
      setError(`${file.name}: ${result.error}`)
      hasError = true
      continue
    }

    processedFiles.push(result.file)
  }

  if (hasError && processedFiles.length === 0) {
    e.target.value = ''
    return
  }

  // ✅ Add processed files
  setFormData({
    ...formData,
    gallery_images: [...formData.gallery_images, ...processedFiles],
    image_titles: [
      ...formData.image_titles,
      ...new Array(processedFiles.length).fill('')
    ]
  })

  setUploadProgress(`Added ${processedFiles.length} file(s)`)
  e.target.value = ''
}

  // Remove a gallery image from the list
  const removeGalleryImage = (index: number) => {
    const newImages = formData.gallery_images.filter((_, i) => i !== index)
    const newTitles = formData.image_titles.filter((_, i) => i !== index)
    setFormData({ 
      ...formData, 
      gallery_images: newImages,
      image_titles: newTitles
    })
  }

  // Handle create
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!formData.event_id) {
      setError('Please select an event')
      return
    }

    if (formData.gallery_images.length === 0) {
      setError('Please select at least one image')
      return
    }

    setUploading(true)
    setUploadProgress('Uploading gallery images...')

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('event_id', formData.event_id)
      
      // Add all gallery images
      formData.gallery_images.forEach((file) => {
        formDataToSend.append('gallery_images[]', file)
      })
      
      // Add thumbnail index (first image is default)
      formDataToSend.append('thumbnail_index', formData.thumbnail_index.toString())
      
      // Add image titles if provided
      formData.image_titles.forEach((title, index) => {
        if (title.trim()) {
          formDataToSend.append('image_titles[]', title)
        } else {
          formDataToSend.append('image_titles[]', `Gallery Image ${index + 1}`)
        }
      })

      const response = await fetch(`${API_BASE_URL}event_gallery_create.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Gallery images uploaded successfully!')
        resetForm()
        setShowAddModal(false)
        setUploadProgress('')
        fetchGalleries()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to upload gallery images')
      }
    } catch (err) {
      setError('Failed to upload gallery images. Please try again.')
      console.error('Error uploading gallery:', err)
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  // Handle update thumbnail
  const handleUpdateThumbnail = async (eventId: number, thumbnailIndex: number) => {
    setError('')
    setSuccess('')
    setUploading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('event_id', eventId.toString())
      formDataToSend.append('thumbnail_index', thumbnailIndex.toString())

      const response = await fetch(`${API_BASE_URL}event_gallery_update.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Thumbnail updated successfully!')
        fetchGalleries()
        if (viewingGallery) {
          getGalleryForEvent(viewingGallery.event_id)
        }
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to update thumbnail')
      }
    } catch (err) {
      setError('Failed to update thumbnail. Please try again.')
      console.error('Error updating thumbnail:', err)
    } finally {
      setUploading(false)
    }
  }

  // Handle add more images to existing gallery
  const handleAddMoreImages = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!updatingGallery) return

    setError('')
    setSuccess('')
    
    const existingImageCount = updatingGallery.images.length
    const newImageCount = formData.gallery_images.length
    const totalCount = existingImageCount + newImageCount

    if (totalCount > MAX_IMAGES) {
      setError(`Total images would be ${totalCount}, but maximum is ${MAX_IMAGES}. You can only add ${MAX_IMAGES - existingImageCount} more image(s).`)
      return
    }

    if (formData.gallery_images.length === 0) {
      setError('Please select at least one image to add')
      return
    }

    setUploading(true)
    setUploadProgress('Adding images to gallery...')

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('event_id', updatingGallery.event_id.toString())
      
      // Add all new gallery images
      formData.gallery_images.forEach((file) => {
        formDataToSend.append('gallery_images[]', file)
      })
      
      // Add thumbnail index (keep existing thumbnail, so use -1 or don't set)
      // Only set if user wants to change thumbnail
      if (formData.thumbnail_index >= 0) {
        formDataToSend.append('thumbnail_index', (existingImageCount + formData.thumbnail_index).toString())
      }
      
      // Add image titles if provided
      formData.image_titles.forEach((title, index) => {
        if (title.trim()) {
          formDataToSend.append('image_titles[]', title)
        } else {
          formDataToSend.append('image_titles[]', `Gallery Image ${existingImageCount + index + 1}`)
        }
      })

      const response = await fetch(`${API_BASE_URL}event_gallery_create.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess(`Successfully added ${formData.gallery_images.length} image(s) to gallery!`)
        resetForm()
        setShowUpdateModal(false)
        setUpdatingGallery(null)
        setUploadProgress('')
        fetchGalleries()
        if (viewingGallery && viewingGallery.event_id === updatingGallery.event_id) {
          getGalleryForEvent(viewingGallery.event_id)
        }
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to add images to gallery')
      }
    } catch (err) {
      setError('Failed to add images to gallery. Please try again.')
      console.error('Error adding images to gallery:', err)
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  // Open update modal
  const openUpdateModal = (gallery: EventGallery) => {
    setUpdatingGallery(gallery)
    setFormData({
      event_id: gallery.event_id.toString(),
      gallery_images: [],
      thumbnail_index: -1, // Don't change thumbnail by default
      image_titles: []
    })
    setShowUpdateModal(true)
  }

  // Handle delete gallery (all images for an event)
  const handleDeleteGallery = async (galleryId: number, eventName: string) => {
    if (!window.confirm(`Are you sure you want to delete the entire gallery for "${eventName}"? This will delete all ${viewingGallery?.images.length || 0} images. This action cannot be undone.`)) {
      return
    }

    setError('')
    setSuccess('')
    setUploading(true)

    try {
      const response = await fetch(`${API_BASE_URL}event_gallery_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gallery_id: galleryId })
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Gallery deleted successfully!')
        setShowViewModal(false)
        setViewingGallery(null)
        fetchGalleries()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete gallery')
      }
    } catch (err) {
      setError('Failed to delete gallery. Please try again.')
      console.error('Error deleting gallery:', err)
    } finally {
      setUploading(false)
    }
  }

  // Handle delete individual image
  const handleDeleteImage = async (imageId: number, imageTitle?: string) => {
    if (!window.confirm(`Are you sure you want to delete this image${imageTitle ? `: "${imageTitle}"` : ''}? This action cannot be undone.`)) {
      return
    }

    // Validate input
    if (!imageId) {
      setError('Image ID is missing. Cannot delete image.')
      return
    }

    setError('')
    setSuccess('')
    setUploading(true)

    try {
      // Delete image by image_id only
      const requestBody = { 
        image_id: Number(imageId)
      }
      console.log('Deleting image with:', requestBody)
      
      const response = await fetch(`${API_BASE_URL}event_gallery_image_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('Delete image response:', result)

      if (result.status) {
        setSuccess('Image deleted successfully!')
        // Refresh the gallery view
        if (viewingGallery) {
          getGalleryForEvent(viewingGallery.event_id)
        } else {
          fetchGalleries()
        }
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete image')
        console.error('Delete image failed:', result)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(`Failed to delete image: ${errorMessage}`)
      console.error('Error deleting image:', err)
    } finally {
      setUploading(false)
    }
  }

  // Get event name by ID
  const getEventName = (eventId: number): string => {
    const event = events.find(e => e.id === eventId)
    return event ? event.title : `Event #${eventId}`
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      event_id: '',
      gallery_images: [],
      thumbnail_index: 0,
      image_titles: []
    })
    setError('')
    setUploadProgress('')
  }

  // Open view modal
  const openViewModal = (gallery: EventGallery) => {
    setViewingGallery(gallery)
    setShowViewModal(true)
  }

  // Get gallery for specific event
  const getGalleryForEvent = async (eventId: number) => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}event_gallery_list.php?event_id=${eventId}`)
      const result = await response.json()
      
      if (result.status && result.data) {
        const gallery: EventGallery = {
          id: result.data[0]?.gallery_id,
          gallery_id: result.data[0]?.gallery_id,
          event_id: eventId,
          images: result.data,
          thumbnail_url: result.data.find((img: GalleryImage) => img.is_thumbnail)?.media_url || 
                        result.data.find((img: GalleryImage) => img.is_thumbnail)?.image_url ||
                        (result.data[0] ? getImageUrl(result.data[0]) || undefined : undefined)
        }
        openViewModal(gallery)
      }
    } catch (err) {
      setError('Failed to fetch gallery images')
      console.error('Error fetching gallery:', err)
    } finally {
      setLoading(false)
    }
  }



  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">Event Gallery Management</h1>
              <button
                onClick={() => {
                  resetForm()
                  setShowAddModal(true)
                }}
                className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Gallery Images</span>
              </button>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg animate-fadeIn">
                <p className="text-green-400">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg animate-fadeIn">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Galleries Grid */}
            {loading ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                <p className="text-gray-400 text-lg">Loading galleries...</p>
              </div>
            ) : galleries.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-400 text-lg">No galleries found. Add gallery images for an event!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleries.map((gallery) => (
                  <div key={gallery.event_id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative h-48 bg-gray-700">
                      {gallery.thumbnail_url ? (
                        <img src={gallery.thumbnail_url} alt={getEventName(gallery.event_id)} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-2 right-2 bg-primary-light text-white px-3 py-1 rounded-lg font-semibold text-sm">
                        {gallery.images.length} {gallery.images.length === 1 ? 'Image' : 'Images'}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-bold text-lg mb-2">{getEventName(gallery.event_id)}</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => getGalleryForEvent(gallery.event_id)}
                          className="flex-1 px-3 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          View Gallery
                        </button>
                        <button
                          onClick={() => openUpdateModal(gallery)}
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors text-sm"
                          title="Add more images"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            // Get gallery_id from gallery object or first image
                            const galleryId = gallery.gallery_id || gallery.id || gallery.images[0]?.gallery_id || gallery.images[0]?.id
                            if (galleryId) {
                              handleDeleteGallery(gallery.event_id, getEventName(gallery.event_id))
                            } else {
                              setError('Gallery ID not found. Cannot delete.')
                            }
                          }}
                          className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm"
                          title="Delete entire gallery"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Gallery Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-3xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add Gallery Images</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-6">
              {/* Event Selection */}
              <div>
                <label htmlFor="event_id" className="block text-white font-semibold mb-2">
                  Select Event *
                </label>
                <select
                  id="event_id"
                  value={formData.event_id}
                  onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                >
                  <option value="">-- Select an Event --</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title} - {event.date}
                    </option>
                  ))}
                </select>
              </div>


              {/* Gallery Images Upload */}
              <div>
                <label htmlFor="gallery_images" className="block text-white font-semibold mb-2">
                  Gallery Images * (Multiple files allowed, Max 50MB each)
                </label>
                <input
                  type="file"
                  id="gallery_images"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryImagesChange}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                />
                <p className="mt-1 text-xs text-gray-400">
                  First image will be automatically selected as thumbnail. You can change it after upload.
                </p>
                <p className="mt-1 text-xs text-red-400 hover:text-red-300">
                  You can upload a maximum of {MAX_IMAGES} images.
                </p>
                {uploadProgress && (
                  <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                )}
                {formData.gallery_images.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm text-primary-light font-semibold">
                      Selected Images ({formData.gallery_images.length}):
                    </p>
                    {formData.gallery_images.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className={`px-2 py-1 rounded text-xs font-semibold ${
                            index === formData.thumbnail_index 
                              ? 'bg-primary-light text-white' 
                              : 'bg-gray-600 text-gray-300'
                          }`}>
                            {index === formData.thumbnail_index ? 'Thumbnail' : `#${index + 1}`}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-white">{file.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                            <input
                              type="text"
                              placeholder="Image title (optional)"
                              value={formData.image_titles[index] || ''}
                              onChange={(e) => {
                                const newTitles = [...formData.image_titles]
                                newTitles[index] = e.target.value
                                setFormData({ ...formData, image_titles: newTitles })
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm rounded bg-gray-600 text-white placeholder-gray-400 border border-gray-500 focus:outline-none focus:border-primary-light"
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {index !== formData.thumbnail_index && (
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, thumbnail_index: index })}
                              className="text-primary-light hover:text-primary-dark text-xs font-semibold"
                              title="Set as thumbnail"
                            >
                              Set Thumbnail
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>




              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Upload Gallery Images'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Gallery Modal */}
      {showViewModal && viewingGallery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-5xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                Gallery: {getEventName(viewingGallery.event_id)}
              </h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => openUpdateModal(viewingGallery)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors text-sm flex items-center space-x-2"
                  title="Add more images"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Images ({viewingGallery.images.length}/{MAX_IMAGES})</span>
                </button>
                <button
                  onClick={() => {
                    // Get gallery_id from gallery object or first image
                    const galleryId = viewingGallery.gallery_id || viewingGallery.id || viewingGallery.images[0]?.gallery_id || viewingGallery.images[0]?.id
                    if (galleryId) {
                      handleDeleteGallery(galleryId, getEventName(viewingGallery.event_id))
                    } else {
                      setError('Gallery ID not found. Cannot delete.')
                    }
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm flex items-center space-x-2"
                  title="Delete entire gallery"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete Gallery</span>
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingGallery(null)
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {viewingGallery.images.map((image, index) => {
                const imageUrl = getImageUrl(image)
                return (
                  <div key={image.id || index} className="relative group">
                    <div className="relative aspect-square bg-gray-700 rounded-lg overflow-hidden">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={image.title || `Gallery Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {image.is_thumbnail && (
                        <div className="absolute top-2 left-2 bg-primary-light text-white px-2 py-1 rounded text-xs font-semibold">
                          Thumbnail
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-2">
                        {!image.is_thumbnail && (
                          <button
                            onClick={() => handleUpdateThumbnail(viewingGallery.event_id, index)}
                            className="opacity-0 group-hover:opacity-100 bg-primary-light hover:bg-primary-dark text-white px-3 py-2 rounded-lg font-semibold transition-opacity text-xs"
                          >
                            Set Thumbnail
                          </button>
                        )}
                        {image.id && (
                          <button
                            onClick={() => {
                              console.log('Delete button clicked - Image ID:', image.id, 'Image:', image)
                              if (image.id) {
                                handleDeleteImage(image.id, image.title)
                              } else {
                                setError('Image ID not found. Cannot delete image.')
                              }
                            }}
                            className="opacity-0 group-hover:opacity-100 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg font-semibold transition-opacity text-xs flex items-center space-x-1"
                            title="Delete this image"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                    {image.title && (
                      <p className="mt-2 text-sm text-gray-300 text-center">{image.title}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Update Gallery Modal - Add More Images */}
      {showUpdateModal && updatingGallery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-3xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                Add More Images to: {getEventName(updatingGallery.event_id)}
              </h2>
              <button
                onClick={() => {
                  setShowUpdateModal(false)
                  setUpdatingGallery(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-4 bg-blue-500/20 border border-blue-500 rounded-lg">
              <p className="text-blue-400 text-sm">
                <strong>Current Images:</strong> {updatingGallery.images.length} / {MAX_IMAGES}
              </p>
              <p className="text-blue-400 text-sm mt-1">
                <strong>You can add:</strong> {MAX_IMAGES - updatingGallery.images.length} more image(s)
              </p>
            </div>

            <form onSubmit={handleAddMoreImages} className="space-y-6">
              {/* Gallery Images Upload */}
              <div>
                <label htmlFor="update_gallery_images" className="block text-white font-semibold mb-2">
                  Add More Images * (Multiple files allowed, Max 50MB each)
                </label>
                <input
                  type="file"
                  id="update_gallery_images"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryImagesChange}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Select images to add to the existing gallery.
                </p>
                <p className="mt-1 text-xs text-red-400">
                  Maximum {MAX_IMAGES} images total. You can add {MAX_IMAGES - updatingGallery.images.length} more.
                </p>
                {uploadProgress && (
                  <p className="mt-2 text-sm text-gray-400">{uploadProgress}</p>
                )}
                {formData.gallery_images.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm text-primary-light font-semibold">
                      New Images to Add ({formData.gallery_images.length}):
                    </p>
                    {formData.gallery_images.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="px-2 py-1 rounded text-xs font-semibold bg-gray-600 text-gray-300">
                            #{updatingGallery.images.length + index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-white">{file.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                            <input
                              type="text"
                              placeholder="Image title (optional)"
                              value={formData.image_titles[index] || ''}
                              onChange={(e) => {
                                const newTitles = [...formData.image_titles]
                                newTitles[index] = e.target.value
                                setFormData({ ...formData, image_titles: newTitles })
                              }}
                              className="mt-1 w-full px-2 py-1 text-sm rounded bg-gray-600 text-white placeholder-gray-400 border border-gray-500 focus:outline-none focus:border-primary-light"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUpdateModal(false)
                    setUpdatingGallery(null)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || formData.gallery_images.length === 0}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading || formData.gallery_images.length === 0
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Adding Images...' : `Add ${formData.gallery_images.length} Image(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventGalleryManagement

